import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';

const StaticPage = ({ title, description, children }) => (
  <>
    <Helmet>
      <title>{title + ' — Skinn Thrive'}</title>
      {description && <meta name="description" content={description} />}
    </Helmet>
    <div
      className="py-10 md:py-14 px-4 text-center"
      style={{ background: 'linear-gradient(135deg, #F5F3FF 0%, #FAE8FF 100%)', borderBottom: '1px solid #E5E7EB' }}
    >
      <p className="text-xs font-bold uppercase tracking-widest text-primary mb-2">Skinnthrive</p>
      <h1 className="text-2xl md:text-3xl font-bold text-ink tracking-tight">{title}</h1>
    </div>
    <div className="max-w-3xl mx-auto px-4 py-10">
      <div className="space-y-5">{children}</div>
    </div>
  </>
);

/** Supports plain text or rich JSX children */
const Section = ({ title, children }) => (
  <div className="bg-white rounded-xl border border-border p-5">
    {title && <h2 className="text-base font-bold text-ink mb-2">{title}</h2>}
    <div className="text-ink-soft text-sm leading-relaxed space-y-2">{children}</div>
  </div>
);

const address = 'G-09 Prime Vivane, L Narayana Reddy Main Rd, Kondappa Layout, Vignan Nagar, Doddanekkundi, Bengaluru, Karnataka – 560037';

// ─── Return & Refund Policy ───────────────────────────────────────────────────

export const ReturnPolicyPage = () => (
  <StaticPage title="Refund & Returns Policy" description="Skinn Thrive refund and returns policy — learn about our 3-day refund window, eligibility conditions, and how to request a refund or exchange.">
    <Section title="Important Notice">
      Cosmetics once sold cannot be returned.
    </Section>

    <Section title="Refund Window">
      <p>You may request a refund within <strong className="text-ink">three days</strong> of receiving the product.</p>
      <p>However, if the product's seal is broken, it will <strong className="text-ink">not</strong> be eligible for
        return and the three-day period will no longer apply.</p>
      <p>A valid reason is required to process a refund — it cannot be based solely on personal preference.</p>
    </Section>

    <Section title="Refunds">
      No refunds are applicable if the order has already been delivered and accepted without issue.
    </Section>

    <Section title="Late or Missing Refunds">
      <p>If you haven't received your refund yet, please follow these steps:</p>
      <ol className="list-decimal list-inside space-y-1 mt-1">
        <li>Check your bank account again.</li>
        <li>Contact your credit card company — it may take some time before the refund is officially posted.</li>
        <li>Contact your bank — there is often processing time before a refund appears.</li>
        <li>If you've done all of the above and still haven't received your refund, email us at{' '}
          <a href="mailto:info@skinnthrive.com" className="text-primary hover:underline">info@skinnthrive.com</a>.
        </li>
      </ol>
    </Section>

    <Section title="Sale Items">
      Only regular-priced items may be refunded. Sale items cannot be refunded.
    </Section>

    <Section title="Exchanges">
      <p>We only replace items if they are defective or damaged. If you need to exchange a defective item, send us
        an email at{' '}
        <a href="mailto:info@skinnthrive.com" className="text-primary hover:underline">info@skinnthrive.com</a>{' '}
        and ship the item to:</p>
      <p className="bg-surface rounded-lg px-4 py-3 mt-2 text-ink font-medium">{address}</p>
    </Section>

    <Section title="Damaged Products">
      If you received a damaged product, please email photos of the product to{' '}
      <a href="mailto:info@skinnthrive.com" className="text-primary hover:underline">info@skinnthrive.com</a>.
      The product will be recalled and returned to the manufacturer for replacement. This process may take up to 30 days.
    </Section>

    <Section title="Shipping Returns">
      <p>To return your product, mail it to:</p>
      <p className="bg-surface rounded-lg px-4 py-3 mt-2 text-ink font-medium">{address}</p>
      <p className="mt-2">You are responsible for paying your own return shipping costs. Shipping costs are
        non-refundable. If you receive a refund, the cost of return shipping will be deducted from your refund.</p>
      <p>Depending on where you live, the time it takes for your exchanged product to reach you may vary.</p>
    </Section>
  </StaticPage>
);

// ─── Privacy Policy ───────────────────────────────────────────────────────────

export const PrivacyPolicyPage = () => (
  <StaticPage title="Privacy Policy" description="Skinn Thrive privacy policy — how we collect, use, and protect your personal information when you shop at skinnthrive.com.">
    <p className="text-xs text-ink-muted text-center -mt-2">Last updated: May 25, 2025</p>

    <Section title="Overview">
      This Privacy Policy describes how Skinnthrive ("Site", "we", "us", or "our") collects, uses, and discloses your
      personal information when you visit, use our services, or make a purchase from skinnthrive.com. By using and
      accessing any of our Services, you agree to this Privacy Policy. If you do not agree, please do not use the
      Services.
    </Section>

    <Section title="Changes to This Policy">
      We may update this Privacy Policy from time to time. We will post the revised Policy on the Site and update the
      "Last updated" date. We may take any other steps required by applicable law.
    </Section>

    <Section title="Information We Collect Directly from You">
      <ul className="list-disc list-inside space-y-1">
        <li>Contact details: name, address, phone number, and email.</li>
        <li>Order information: name, billing/shipping address, payment confirmation, email, and phone.</li>
        <li>Account information: username, password, and security details.</li>
        <li>Customer support information: messages and queries you send us.</li>
      </ul>
    </Section>

    <Section title="Usage Data & Cookies">
      <p>We automatically collect information about your interaction with the Services using cookies, pixels, and similar
        technologies. This includes device and browser information, network details, IP address, and browsing behaviour.</p>
      <p className="mt-2">Most browsers accept cookies by default. You can disable them in your browser settings, but doing
        so may affect functionality.</p>
    </Section>

    <Section title="Information from Third Parties">
      We may receive information from vendors and service providers who support our Site, our payment processors, and
      third-party advertising partners.
    </Section>

    <Section title="How We Use Your Information">
      <ul className="list-disc list-inside space-y-1">
        <li><strong className="text-ink">Order fulfilment:</strong> process payments, ship orders, and handle returns.</li>
        <li><strong className="text-ink">Marketing:</strong> send promotional emails and personalised ads (opt-out available).</li>
        <li><strong className="text-ink">Fraud prevention:</strong> detect and act on suspicious or illegal activity.</li>
        <li><strong className="text-ink">Service improvement:</strong> provide customer support and improve our platform.</li>
      </ul>
    </Section>

    <Section title="How We Share Your Information">
      <p>We do not sell your personal information. We may share it with:</p>
      <ul className="list-disc list-inside space-y-1 mt-1">
        <li>Vendors performing services on our behalf (payment processors, logistics partners, analytics).</li>
        <li>Business and marketing partners in accordance with their own privacy policies.</li>
        <li>Affiliates within our corporate group.</li>
        <li>Authorities when required by law or to enforce our terms.</li>
      </ul>
    </Section>

    <Section title="Third-Party Websites">
      Our Site may link to external websites. We are not responsible for the privacy practices or content of those sites.
    </Section>

    <Section title="Children's Data">
      Our Services are not intended for children. We do not knowingly collect personal information from anyone under 16.
      If you believe a child has provided us with their data, please contact us to have it deleted.
    </Section>

    <Section title="Your Rights">
      Depending on your location, you may have the right to access, delete, correct, or port your personal information;
      restrict processing; withdraw consent; or appeal a decision. To exercise these rights, contact us at the email below.
    </Section>

    <Section title="Contact">
      For privacy-related queries, email us at{' '}
      <a href="mailto:info@skinnthrive.com" className="text-primary hover:underline">info@skinnthrive.com</a>.
    </Section>
  </StaticPage>
);

// ─── Terms & Conditions ───────────────────────────────────────────────────────

export const TermsPage = () => (
  <StaticPage title="Terms of Service" description="Skinn Thrive terms of service — conditions for using our website and purchasing our products.">
    <Section title="General Information">
      This is the official website of Skinnthrive, developed to provide general public information. The documents and
      information displayed are for reference purposes only.
    </Section>

    <Section title="No Warranty">
      The content on this site is updated on a continual basis. While Skinnthrive attempts to keep information accurate
      and timely, no representations, warranties or guarantees are made as to the accuracy, adequacy, reliability,
      completeness, suitability or applicability of the information, text, graphics, hyperlinks, and other items
      contained on this site.
    </Section>

    <Section title="Intellectual Property">
      Unless otherwise stated, copyright and all intellectual property rights in all material on this site — including
      text, audio, video, graphical images, trademarks, and logos — are the property of Skinnthrive, its affiliates,
      and associates, protected under applicable Indian laws. Commercial use of any web material is prohibited without
      written permission.
    </Section>

    <Section title="Downloads & Liability">
      Any material downloaded through use of the site is done at your own discretion and risk. Skinnthrive is neither
      responsible nor liable for any viruses, contamination of your system, damages, claims, delays, inaccuracies,
      errors, or omissions arising out of your use of the site.
    </Section>

    <Section title="Third-Party Links">
      Some hyperlinks on this site may lead to external resources, including websites of Skinnthrive affiliates.
      Skinnthrive is not responsible for the content of any such external links. References to external links should
      not be construed as an endorsement.
    </Section>

    <Section title="User Submissions">
      Should any visitor respond with information including feedback, questions, comments, or suggestions, Skinnthrive
      shall deal with the same in the manner it deems fit.
    </Section>

    <Section title="RTO & Repeat Returns Policy">
      If an RTO (Return to Origin) is generated without a valid reason and the customer no longer requires the product,
      and if this behaviour is repeated by the same customer — even using different email IDs — Skinnthrive reserves the
      right to block the customer ID associated with such activity.
    </Section>

    <Section title="Governing Law">
      These terms are governed by the laws of India. Any disputes are subject to the jurisdiction of courts in Surat,
      Gujarat.
    </Section>
  </StaticPage>
);

// ─── Shipping Policy ──────────────────────────────────────────────────────────

export const ShippingPage = () => (
  <StaticPage title="Shipping Policy" description="Skinn Thrive shipping policy — delivery timelines, courier partners, and return shipping guidelines for India.">
    <Section title="Courier Partners">
      We provide shipping through third-party courier partners. Shipping times may vary depending on location and
      circumstances — typically between <strong className="text-ink">3 to 15 business days</strong>.
    </Section>

    <Section title="Damaged Products">
      If you receive a damaged product, please email photos of the item to{' '}
      <a href="mailto:info@skinnthrive.com" className="text-primary hover:underline">info@skinnthrive.com</a>.
      The product will be recalled, returned to the manufacturer, and replaced. This process may take up to 30 days.
    </Section>

    <Section title="Returns Shipping Address">
      <p>To return your product, ship it to:</p>
      <p className="bg-surface rounded-lg px-4 py-3 mt-2 text-ink font-medium">{address}</p>
    </Section>

    <Section title="Return Shipping Costs">
      You are responsible for paying your own return shipping costs. Shipping costs are non-refundable. If you receive a
      refund, the cost of return shipping will be deducted from your refund. Depending on where you live, the time it
      takes for your exchanged product to reach you may vary.
    </Section>
  </StaticPage>
);

// ─── Cancellation Policy ─────────────────────────────────────────────────────

export const CancellationPage = () => (
  <StaticPage title="Cancellation Policy" description="Skinn Thrive cancellation policy — cancel your order within 2 hours of placing it. Learn how to cancel and receive a refund for prepaid orders.">
    <Section title="Order Cancellation">
      You can cancel your order within 2 hours of placing it, as long as it hasn't been processed for dispatch. After
      that, cancellations are not possible.
    </Section>
    <Section title="How to Cancel">
      Log in to your account and visit 'My Orders', or contact us at{' '}
      <a href="mailto:info@skinnthrive.com" className="text-primary hover:underline">info@skinnthrive.com</a>{' '}
      with your Order ID within the cancellation window.
    </Section>
    <Section title="Refund on Cancellation">
      For prepaid orders, the full amount will be refunded within 5–7 business days to your original payment method.
      COD orders do not require a refund.
    </Section>
    <Section title="Seller-Initiated Cancellations">
      In rare cases, we may cancel your order due to stock unavailability or pricing errors. You will be notified and
      refunded immediately if this occurs.
    </Section>
  </StaticPage>
);

// ─── Sitemap ─────────────────────────────────────────────────────────────────

export const SitemapPage = () => (
  <StaticPage title="Sitemap">
    <div className="grid grid-cols-2 md:grid-cols-3 gap-6">
      {[
        { title: 'Shopping', links: [['Home', '/'], ['Shop All', '/shop'], ['New Arrivals', '/new-arrivals'], ['Brands', '/brands'], ['Wishlist', '/wishlist']] },
        { title: 'Account', links: [['Sign In', '/login'], ['Create Account', '/register'], ['My Account', '/account'], ['My Orders', '/account/orders'], ['Track Order', '/track-order']] },
        { title: 'Info', links: [['About Us', '/about'], ['Contact Us', '/contact'], ['Refund Policy', '/return-policy'], ['Shipping', '/shipping'], ['Privacy Policy', '/privacy-policy'], ['Terms', '/terms'], ['Cancellation', '/cancellation']] },
      ].map(group => (
        <div key={group.title}>
          <h3 className="font-bold text-ink mb-3">{group.title}</h3>
          <ul className="space-y-2">
            {group.links.map(([label, to]) => (
              <li key={to}><Link to={to} className="text-sm text-primary hover:underline">{label}</Link></li>
            ))}
          </ul>
        </div>
      ))}
    </div>
  </StaticPage>
);

// ─── 404 ──────────────────────────────────────────────────────────────────────

export const NotFoundPage = () => (
  <>
    <Helmet><title>Page Not Found — Skinnthrive</title></Helmet>
    <div className="max-w-2xl mx-auto px-4 py-20 text-center">
      <div className="text-8xl font-black text-primary-light mb-4">404</div>
      <h1 className="text-3xl font-bold text-ink mb-3 tracking-tight">Page Not Found</h1>
      <p className="text-ink-soft mb-8">The page you're looking for doesn't exist or has been moved.</p>
      <div className="flex gap-3 justify-center">
        <Link to="/" className="btn-primary">Go Home</Link>
        <Link to="/shop" className="btn-outline">Shop Products</Link>
      </div>
    </div>
  </>
);
